import { Injectable, computed, inject, signal } from '@angular/core'
import { BasemapApiKeyService } from './basemap-api-key.service'

export type BasemapKeyDialogMode = 'enter' | 'change'

export interface BasemapKeyDialogRequest {
  mode: BasemapKeyDialogMode
  /** The map control that opened the dialog; lets it be cancelled when that control is destroyed. */
  owner: object
}

/**
 * Drives the single, app-wide MapTiler API-key dialog (hosted once in
 * AppComponent). Replaces the old blocking browser prompt: callers await
 * `open()`, which resolves with the stored key or null when dismissed.
 */
@Injectable({ providedIn: 'root' })
export class BasemapApiKeyDialogService {
  private readonly apiKeySvc = inject(BasemapApiKeyService)

  readonly request = signal<BasemapKeyDialogRequest | null>(null)
  readonly isOpen = computed(() => this.request() !== null)

  /**
   * Session-only: once the user dismisses a "missing key" notice, no map shows
   * one again until reload. Deliberately not persisted.
   */
  readonly noticeDismissed = signal(false)

  private pending: Promise<string | null> | null = null
  private resolvePending: ((key: string | null) => void) | null = null

  /** A second call while one is pending returns the pending promise and ignores its arguments. */
  open(mode: BasemapKeyDialogMode, owner: object): Promise<string | null> {
    if (this.pending) return this.pending
    this.pending = new Promise<string | null>(resolve => { this.resolvePending = resolve })
    this.request.set({ mode, owner })
    return this.pending
  }

  /** Stores the trimmed key and closes. False (dialog stays open) when storing fails. */
  submit(key: string): boolean {
    const trimmed = key.trim()
    if (!this.apiKeySvc.storeKey(trimmed)) return false
    this.settle(trimmed)
    return true
  }

  /** Closes without storing anything. */
  cancel(): void {
    this.settle(null)
  }

  cancelIfOwnedBy(owner: object): void {
    if (this.request()?.owner === owner) this.cancel()
  }

  private settle(result: string | null): void {
    const resolve = this.resolvePending
    this.pending = null
    this.resolvePending = null
    this.request.set(null)
    resolve?.(result)
  }
}
